import { StageType } from "../../../graphql/types";
import type {
  RoiCalculatorFormData,
  RoiFarmGateInputs,
  RoiStage,
} from "../models/RoiCalculator";
import type { RoiFormInputs } from "./roiOutMapper";
import {
  FARM_GATE_FIELD_MAP,
  normalizeStageType,
  parseAwsJson,
  RoiFormData,
} from "./roiStageData";

const toFormValue = (raw: unknown): string =>
  typeof raw === "number" && Number.isFinite(raw) ? String(raw) : "";

export const parseRoiFormInputs = (raw: unknown): RoiFormInputs | null =>
  parseAwsJson<RoiFormInputs>(raw);

const buildStages = (
  inputs: RoiFormInputs,
  formData: RoiFormData
): RoiStage[] =>
  formData.feedBearingStages.map((stage) => {
    const key = normalizeStageType(stage.stageType) as StageType;
    return {
      stageId: stage.stageId ?? "",
      stageName: stage.stageName ?? "",
      stageType: key,
      baselineInputs: {
        feedCost: toFormValue(inputs.feedCostPerTonne?.baseline?.[key]),
      },
      interventionInputs: {
        feedCost: toFormValue(inputs.feedCostPerTonne?.intervention?.[key]),
      },
    };
  });

const buildFarmGateSide = (
  side: Record<string, number> | undefined,
  terminalStageType: string | null
): RoiFarmGateInputs => {
  const pairs =
    FARM_GATE_FIELD_MAP[normalizeStageType(terminalStageType) as StageType];
  if (!pairs || !side) return {};
  return pairs.reduce<RoiFarmGateInputs>((acc, { outputField, formField }) => {
    const value = side[outputField];
    if (value !== undefined) acc[formField] = toFormValue(value);
    return acc;
  }, {});
};

// eslint-disable-next-line import/prefer-default-export
export const fromRoiFormInputs = (
  inputs: RoiFormInputs,
  formData: RoiFormData
): RoiCalculatorFormData => ({
  currency: inputs.currency ?? formData.currency,
  inclusionCostOfSolution: toFormValue(inputs.inclusionCostOfSolution),
  stages: buildStages(inputs, formData),
  farmGate: {
    baselineInputs: buildFarmGateSide(
      inputs.farmGate?.baseline,
      formData.terminalOutputStageType
    ),
    interventionInputs: buildFarmGateSide(
      inputs.farmGate?.intervention,
      formData.terminalOutputStageType
    ),
  },
  carbonPricePerTonne: toFormValue(inputs.carbonPricePerTonne),
});
